import {
  DETAIL_ANIMAL_SEARCH_TYPES,
  SEARCH_TYPE,
} from '@/constants/searchType';
import { useEffect, useMemo } from 'react';
import type { SelectChangeEvent } from '@mui/material';
import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
} from '@/primitives';
import useFilterStore from '@/publicSearch/store/useFilterStore';
import { getOperatorsByField } from '../helpers';
import { FilterInput, FieldOption, OperatorOption } from '../../constants';
import {
  FILTER_INPUT_TYPE,
  InputTypeByField,
  OptionsByField,
} from '../constants';
import AnimalIdentMultiSelect from '../../mating/AnimalIdentMultiSelect';
import MemberIdentMultiSelect from '../../mating/MemberIdentMultiSelect';
import BasicSearchValue from './BasicSearchValue';
import CalvingYearChipInput from './CalvingYearChipInput';
import { IDENT_CHIP_PLACEHOLDER } from '../quickSearchCopy';
import { useSearchType } from '@/hooks/publicSearch/useSearchType';
import { legacyFilterToChipValue } from '../animalIdentChipFilter';
import BasicSearchConditionDatePicker from './BasicSearchConditionDatePicker';
import { useBasicSearchConditionHandlers } from './useBasicSearchConditionHandlers';

interface Props {
  field: (typeof FieldOption)[keyof typeof FieldOption];
  getUniqueFilters: () => FilterInput[];
  /** When provided (e.g. in Basic Search dialog), use these instead of store so the first select works and we don't trigger grid/query until Show results */
  filtersOverride?: FilterInput[];
  setFiltersOverride?: (filters: FilterInput[]) => void;
}

export default function BasicSearchCondition({
  field,
  getUniqueFilters,
  filtersOverride,
  setFiltersOverride,
}: Props) {
  const searchType = useSearchType();
  const store = useFilterStore(searchType);
  const filtersToUse = filtersOverride ?? store.filters;
  const setFiltersToUse = setFiltersOverride ?? store.setFilters;
  const updateCommonFilterOperatorToUse = setFiltersOverride
    ? () => {}
    : store.updateCommonFilterOperator;

  const { operator = '', value = '' } = useMemo(
    () =>
      filtersToUse.find((f) => f.field === field.value) || ({} as FilterInput),
    [filtersToUse, field],
  );

  const operators = useMemo(() => getOperatorsByField(field.value), [field]);
  const isSexField = field.value === FieldOption.sex.value;
  const isDetailAnimalsSearch = DETAIL_ANIMAL_SEARCH_TYPES.includes(searchType);
  const isAnimalQuickSearchField =
    (searchType === SEARCH_TYPE.ANIMAL || isDetailAnimalsSearch) &&
    (field.value === FieldOption.searchTerm.value ||
      field.value === FieldOption.dam.value ||
      field.value === FieldOption.sire.value);
  const isMemberQuickSearchField =
    (searchType === SEARCH_TYPE.ANIMAL || isDetailAnimalsSearch) &&
    (field.value === FieldOption.breeder.value ||
      field.value === FieldOption.currentOwner.value);
  const isMemberSearchField =
    searchType === SEARCH_TYPE.MEMBERS &&
    field.value === FieldOption.member.value;
  const isQuickSearchField =
    isAnimalQuickSearchField || isMemberQuickSearchField || isMemberSearchField;
  const isTattooField = field.value === FieldOption.tattoo.value;
  const isCalvingYearField = field.value === FieldOption.calvingYear.value;
  const isRegistrationStatusField =
    field.value === FieldOption.registrationStatus.value;
  const isMembershipTypeField =
    field.value === FieldOption.membershipType.value;
  const isStudPrefixField = field.value === FieldOption.herdPrefix.value;
  const isStudNoField = field.value === FieldOption.memberIdent.value;
  const type = InputTypeByField[field.value] || 'text';
  const isDateField = field.value === FieldOption.dateOfBirth.value;
  const isBetweenOperator =
    operator === OperatorOption.between.value && type === 'date';
  const displayLabel = (() => {
    if (field.value === FieldOption.sire.value) return 'Sire';
    if (field.value === FieldOption.dam.value) return 'Dam';
    return field.label;
  })();

  // When registration status has a value, ensure operator is equals (selector is hidden)
  useEffect(() => {
    if (
      !isRegistrationStatusField ||
      !value?.trim() ||
      operator === OperatorOption.equals.value
    ) {
      return;
    }
    const uniqueFilters = getUniqueFilters();
    const match = uniqueFilters.find((f) => f.field === field.value);
    if (match) {
      match.operator = OperatorOption.equals.value;
      setFiltersToUse([...uniqueFilters]);
    }
  }, [
    isRegistrationStatusField,
    value,
    operator,
    field.value,
    getUniqueFilters,
    setFiltersToUse,
  ]);

  useEffect(() => {
    const implicitOperator = (() => {
      if (isMembershipTypeField) return OperatorOption.equals.value;
      if (isStudPrefixField || isStudNoField) {
        return OperatorOption.startsWith.value;
      }
      return null;
    })();
    if (!implicitOperator || !value?.trim() || operator === implicitOperator)
      return;
    const uniqueFilters = getUniqueFilters();
    const match = uniqueFilters.find((f) => f.field === field.value);
    if (match) {
      match.operator = implicitOperator;
      setFiltersToUse([...uniqueFilters]);
    }
  }, [
    isMembershipTypeField,
    isStudPrefixField,
    isStudNoField,
    value,
    operator,
    field.value,
    getUniqueFilters,
    setFiltersToUse,
  ]);

  const {
    rangeInputs,
    dateRangeInputs,
    handleChangeOperator,
    handleChangeValue,
    handleRangeChange,
    handleMemberIdentChipChange,
    handleAnimalIdentChipChange,
    handleSexValueChange,
    handleTattooValueChange,
    handleCalvingYearValueChange,
    handleRegistrationStatusValueChange,
    handleDateRangeChange,
  } = useBasicSearchConditionHandlers({
    field,
    getUniqueFilters,
    setFiltersToUse,
    updateCommonFilterOperatorToUse,
    operator,
    value,
    isBetweenOperator,
    isDateField,
  });

  const animalIdentChipValue = useMemo(
    () => legacyFilterToChipValue(operator ?? '', value ?? ''),
    [operator, value],
  );

  const memberIdentChipValue = animalIdentChipValue;

  function renderValue(value: unknown) {
    if (!value) {
      return (
        <Typography
          variant="titlemedium"
          sx={{
            display: { xs: 'none', sm: 'block' },
            color: 'inherit',
            opacity: 0.5,
          }}
        >
          Select value
        </Typography>
      );
    }
    const found = operators.find((op) => op.value === value);
    return (
      <Typography variant="titlemedium">
        {found?.label ?? (value as string)}
      </Typography>
    );
  }

  return (
    <Stack
      sx={{
        flexDirection: { md: 'row' },
        gap: { xs: '6px', md: '12px' },
        mb: { xs: '16px', md: '16px' },
        ...(!isDateField && { height: { md: '56px' } }),
      }}
    >
      {(() => {
        if (isQuickSearchField) {
          if (isAnimalQuickSearchField) {
            return (
              <AnimalIdentMultiSelect
                value={animalIdentChipValue}
                onChange={handleAnimalIdentChipChange}
                label={displayLabel}
                placeholder={IDENT_CHIP_PLACEHOLDER}
                showExplicitWildcardOptions
                sexFilter={(() => {
                  if (field.value === FieldOption.sire.value) return 'M';
                  if (field.value === FieldOption.dam.value) return 'F';
                  return undefined;
                })()}
                sx={{
                  flex: 1,
                  '& .MuiOutlinedInput-root.MuiAutocomplete-inputRoot': {
                    minHeight: '56px',
                    fieldset: {
                      borderWidth: 1,
                      borderStyle: 'solid',
                      borderColor: 'grey.500',
                      borderRadius: '8px',
                    },
                    paddingLeft: '12px',
                    paddingRight: '40px !important',
                  },
                  '& .MuiAutocomplete-input': {
                    fontSize: '16px',
                    fontWeight: 500,
                  },
                }}
              />
            );
          }
          return (
            <MemberIdentMultiSelect
              value={memberIdentChipValue}
              onChange={handleMemberIdentChipChange}
              label={displayLabel}
              placeholder={IDENT_CHIP_PLACEHOLDER}
              showExplicitWildcardOptions
              sx={{
                flex: 1,
                '& .MuiOutlinedInput-root.MuiAutocomplete-inputRoot': {
                  minHeight: '56px',
                  fieldset: {
                    borderWidth: 1,
                    borderStyle: 'solid',
                    borderColor: 'grey.500',
                    borderRadius: '8px',
                  },
                  paddingLeft: '12px',
                  paddingRight: '40px !important',
                },
                '& .MuiAutocomplete-input': {
                  fontSize: '16px',
                  fontWeight: 500,
                },
              }}
            />
          );
        }
        if (isSexField) {
          return (
            <BasicSearchValue
              fieldLabel={displayLabel}
              selectProps={{
                options: OptionsByField[field.value]!,
                multiple: true,
                sx: {
                  flex: 1,
                  '.MuiSelect-select': {
                    px: '12px',
                  },
                  fieldset: {
                    borderWidth: 1,
                    borderStyle: 'solid',
                    borderColor: 'grey.500',
                    borderRadius: '8px',
                  },
                  svg: {
                    right: '10px',
                    color: 'text.primary',
                  },
                },
              }}
              textFieldProps={{
                placeholder: 'Select value',
                sx: { display: 'none' },
              }}
              type={FILTER_INPUT_TYPE.DROPDOWN}
              value={value}
              onChange={handleSexValueChange}
            />
          );
        }
        if (isDateField) {
          return (
            <Stack sx={{ flex: 1, gap: '4px' }}>
              <Typography
                variant="labelsmall"
                sx={{ color: 'text.secondary', fontWeight: 500 }}
              >
                {displayLabel}
              </Typography>
              <Stack sx={{ flexDirection: 'row', gap: '12px' }}>
                <BasicSearchConditionDatePicker
                  label="From"
                  value={dateRangeInputs.min || ''}
                  onChange={(nextValue) =>
                    handleDateRangeChange('min', nextValue)
                  }
                />
                <BasicSearchConditionDatePicker
                  label="To"
                  value={dateRangeInputs.max || ''}
                  onChange={(nextValue) =>
                    handleDateRangeChange('max', nextValue)
                  }
                />
              </Stack>
            </Stack>
          );
        }
        if (isTattooField) {
          return (
            <BasicSearchValue
              textFieldProps={{
                label: displayLabel,
                InputLabelProps: { shrink: true },
                placeholder: 'Enter value',
                sx: {
                  flex: 1,
                  fieldset: {
                    borderWidth: 1,
                    borderStyle: 'solid',
                    borderColor: 'grey.500',
                    borderRadius: '8px',
                  },
                  input: {
                    px: '12px',
                    fontSize: '16px',
                    fontWeight: 500,
                  },
                },
              }}
              type="text"
              value={value}
              onChange={handleTattooValueChange}
            />
          );
        }
        if (isCalvingYearField) {
          return (
            <CalvingYearChipInput
              value={value}
              onChange={handleCalvingYearValueChange}
              label={displayLabel}
              sx={{
                '& .MuiOutlinedInput-root': {
                  minHeight: '56px',
                  fieldset: {
                    borderWidth: 1,
                    borderStyle: 'solid',
                    borderColor: 'grey.500',
                    borderRadius: '8px',
                  },
                  '& .MuiOutlinedInput-input': {
                    px: '12px',
                    fontSize: '16px',
                    fontWeight: 500,
                  },
                },
              }}
            />
          );
        }
        if (isRegistrationStatusField) {
          return (
            <BasicSearchValue
              fieldLabel={displayLabel}
              selectProps={{
                options: OptionsByField[field.value]!,
                multiple: false,
                sx: {
                  flex: 1,
                  '.MuiSelect-select': {
                    px: '12px',
                  },
                  fieldset: {
                    borderWidth: 1,
                    borderStyle: 'solid',
                    borderColor: 'grey.500',
                    borderRadius: '8px',
                  },
                  svg: {
                    right: '10px',
                    color: 'text.primary',
                  },
                },
              }}
              textFieldProps={{
                placeholder: 'Select value',
                sx: { display: 'none' },
              }}
              type={FILTER_INPUT_TYPE.DROPDOWN}
              value={value}
              onChange={handleRegistrationStatusValueChange}
            />
          );
        }
        if (isMembershipTypeField) {
          return (
            <BasicSearchValue
              fieldLabel={displayLabel}
              selectProps={{
                options: OptionsByField[field.value]!,
                multiple: false,
                sx: {
                  flex: 1,
                  '.MuiSelect-select': {
                    px: '12px',
                  },
                  fieldset: {
                    borderWidth: 1,
                    borderStyle: 'solid',
                    borderColor: 'grey.500',
                    borderRadius: '8px',
                  },
                  svg: {
                    right: '10px',
                    color: 'text.primary',
                  },
                },
              }}
              textFieldProps={{
                placeholder: 'Select value',
                sx: { display: 'none' },
              }}
              type={FILTER_INPUT_TYPE.DROPDOWN}
              value={value}
              onChange={handleRegistrationStatusValueChange}
            />
          );
        }
        if (isStudPrefixField || isStudNoField) {
          return (
            <BasicSearchValue
              textFieldProps={{
                label: displayLabel,
                InputLabelProps: { shrink: true },
                placeholder: 'Enter value',
                sx: {
                  flex: 1,
                  fieldset: {
                    borderWidth: 1,
                    borderStyle: 'solid',
                    borderColor: 'grey.500',
                    borderRadius: '8px',
                  },
                  input: {
                    px: '12px',
                    fontSize: '16px',
                    fontWeight: 500,
                  },
                },
              }}
              type="text"
              value={value}
              onChange={handleTattooValueChange}
            />
          );
        }
        return (
          <Stack sx={{ flexDirection: 'row', gap: '12px', flex: 1 }}>
            <FormControl sx={{ minWidth: { xs: '56px', sm: '186px' } }}>
              <InputLabel shrink id={`bsc-op-label-${field.value}`}>
                {displayLabel}
              </InputLabel>
              <Select
                displayEmpty
                label={displayLabel}
                labelId={`bsc-op-label-${field.value}`}
                notched
                renderValue={renderValue}
                sx={{
                  '.MuiSelect-select': {
                    px: '12px',
                    ...(!operator && value && { color: 'error.main' }),
                  },
                  fieldset: {
                    borderWidth: 1,
                    borderStyle: 'solid',
                    borderColor: 'grey.500',
                    ...(!operator && value && { borderColor: 'error.main' }),
                    borderRadius: '8px',
                  },
                  svg: {
                    right: '10px',
                    color: 'text.primary',
                    ...(!operator && value && { color: 'error.main' }),
                  },
                }}
                value={operator || ''}
                onChange={(e: SelectChangeEvent<unknown>) =>
                  handleChangeOperator(e.target.value as string)
                }
              >
                <MenuItem sx={{ height: '36px' }} value="" />
                {operators.map(({ label, value }) => (
                  <MenuItem key={value} value={value}>
                    <Typography variant="titlemedium">{label}</Typography>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {(() => {
              if (isBetweenOperator) {
                return (
                  <Stack sx={{ flexDirection: 'row', gap: '12px', flex: 1 }}>
                    {type === 'date' ? (
                      <>
                        <BasicSearchConditionDatePicker
                          label="From"
                          value={rangeInputs.min || ''}
                          onChange={(nextValue) =>
                            handleRangeChange('min', nextValue)
                          }
                        />
                        <BasicSearchConditionDatePicker
                          label="To"
                          value={rangeInputs.max || ''}
                          onChange={(nextValue) =>
                            handleRangeChange('max', nextValue)
                          }
                        />
                      </>
                    ) : (
                      <>
                        <BasicSearchValue
                          textFieldProps={{
                            placeholder: 'Min',
                            sx: {
                              flex: 1,
                              fieldset: {
                                borderWidth: 1,
                                borderStyle: 'solid',
                                borderColor: 'grey.500',
                                borderRadius: '8px',
                              },
                              input: {
                                px: '12px',
                                color: 'text.primary',
                                fontSize: '16px',
                                fontWeight: 500,
                              },
                            },
                          }}
                          type={type}
                          value={rangeInputs.min || ''}
                          onChange={(nextValue) =>
                            handleRangeChange('min', nextValue)
                          }
                        />
                        <BasicSearchValue
                          textFieldProps={{
                            placeholder: 'Max',
                            sx: {
                              flex: 1,
                              fieldset: {
                                borderWidth: 1,
                                borderStyle: 'solid',
                                borderColor: 'grey.500',
                                borderRadius: '8px',
                              },
                              input: {
                                px: '12px',
                                color: 'text.primary',
                                fontSize: '16px',
                                fontWeight: 500,
                              },
                            },
                          }}
                          type={type}
                          value={rangeInputs.max || ''}
                          onChange={(nextValue) =>
                            handleRangeChange('max', nextValue)
                          }
                        />
                      </>
                    )}
                  </Stack>
                );
              }
              if (type === 'date') {
                return (
                  <BasicSearchConditionDatePicker
                    label="Value"
                    value={value}
                    onChange={handleChangeValue}
                  />
                );
              }
              return (
                <BasicSearchValue
                  selectProps={{
                    options: OptionsByField[field.value]!,
                    multiple: false,
                    sx: {
                      display:
                        operator === OperatorOption.isNotEmpty.value
                          ? 'none'
                          : 'flex',
                      flex: 1,
                      '.MuiSelect-select': {
                        px: '12px',
                      },
                      fieldset: {
                        borderWidth: 1,
                        borderStyle: 'solid',
                        borderColor: 'grey.500',
                        borderRadius: '8px',
                      },
                      svg: {
                        right: '10px',
                        color: 'text.primary',
                      },
                    },
                  }}
                  textFieldProps={{
                    placeholder: 'Enter value',
                    sx: {
                      display:
                        operator === OperatorOption.isNotEmpty.value
                          ? 'none'
                          : 'flex',
                      flex: 1,
                      fieldset: {
                        borderWidth: 1,
                        borderStyle: 'solid',
                        borderColor: 'grey.500',
                        borderRadius: '8px',
                      },
                      input: {
                        px: '12px',
                        color: 'text.primary',
                        fontSize: '16px',
                        fontWeight: 500,
                      },
                    },
                  }}
                  type={type}
                  value={value}
                  onChange={handleChangeValue}
                />
              );
            })()}
          </Stack>
        );
      })()}
    </Stack>
  );
}
